import { useQuery } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";

import { DefaultResponseWithData } from "../models/system";

interface StockPermissions {
  /**
   * The Stock:AdminOnlyChanges setting applies to this user: they can't delete, scrap, restore or
   * return stock, change a price that's already set, discount a sale, or crush a vehicle with
   * parts still on it. The API enforces it - this only hides what it would refuse.
   */
  restricted: boolean;
}

export const STOCK_PRICE_LOCKED_MESSAGE = "Only an admin can change a price that's already set.";
export const STOCK_DISCOUNT_LOCKED_MESSAGE = "Only an admin can apply a discount.";

/**
 * Asks the API rather than reading the setting and the admin claim here, so the two can't disagree.
 * Falls back to not restricted while loading or if the call fails - the API still refuses, and
 * defaulting the other way would blink every delete button off on page load for every customer.
 */
export default function useStockPermissions(): StockPermissions {
  const { get } = useApiHelper();

  const permissions = useQuery({
    // Its own key, not under "part" - part mutations invalidate that prefix constantly.
    queryKey: ["stockPermissions"],
    queryFn: () => get<DefaultResponseWithData<boolean>>("/part/stock-permissions"),
    staleTime: 5 * 60 * 1000,
  });

  return {
    restricted: permissions.data?.data === true,
  };
}
